import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLayoutEffect, useRef, useState } from 'react';
import { notify } from '@/lib/notify';
import { AuthLayout } from '@/layouts/AuthLayout';
import { apiClient } from '@/api/client';
import { isApiError } from '@/api/errors';

/**
 * Password reset. identity-service mails `/reset-password?token=…` (30 min,
 * single use; requesting a new link invalidates older ones).
 *
 * The token is a credential. On a direct link it is captured into component
 * memory ONLY (never localStorage/sessionStorage), and the query string and any
 * fragment are removed with a history *replace* before the first paint, so the
 * token is not in the address bar, the history entry, later Referers (also
 * blocked by the page's no-referrer policy) or logs. It is never rendered and
 * never put in error messages. A reload therefore drops it by design; the user
 * is told to open the email link again or request a new one.
 *
 * The API answers invalid, expired and already-used tokens with the same 400
 * TOKEN_INVALID_OR_EXPIRED. "Already used" is shown only when THIS page had
 * already submitted the token (e.g. a retry after a network failure).
 */

const TOKEN_SHAPE = /^[A-Za-z0-9_-]{20,200}$/;
/** Non-secret marker: a reset link was opened in this tab (explains a reload). */
export const RESET_LINK_FLAG = 'nh.resetLinkOpened';

const requestSchema = z.object({ email: z.string().email('Enter a valid email address') });
const confirmSchema = z
  .object({
    newPassword: z.string().min(12, 'At least 12 characters').max(128, 'At most 128 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

type RequestValues = z.infer<typeof requestSchema>;
type ConfirmValues = z.infer<typeof confirmSchema>;
type Phase = 'capturing' | 'request' | 'form' | 'invalid' | 'used' | 'gone';

const fieldClass =
  'h-12 w-full rounded-lg border border-border bg-surface px-4 pr-16 text-base text-fg shadow-sm transition placeholder:text-fg-subtle focus:border-brand focus:ring-2 focus:ring-brand/30';
const buttonClass =
  'h-12 w-full rounded-lg bg-brand px-4 font-semibold text-brand-fg shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60';

function readFlag(): boolean {
  try { return sessionStorage.getItem(RESET_LINK_FLAG) === '1'; } catch { return false; }
}
function writeFlag(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(RESET_LINK_FLAG, '1');
    else sessionStorage.removeItem(RESET_LINK_FLAG);
  } catch { /* best effort */ }
}

export function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasLinkInUrl = location.search !== '' || location.hash !== '';
  const tokenRef = useRef<string | null>(null);
  const attemptedRef = useRef(false);
  const [phase, setPhase] = useState<Phase>(() => (hasLinkInUrl ? 'capturing' : readFlag() ? 'gone' : 'request'));
  const [temporaryError, setTemporaryError] = useState<string | null>(null);

  // Capture into memory, then strip query + fragment from the URL and this
  // history entry before the browser paints. A StrictMode re-run sees the same
  // URL and captures the same token.
  useLayoutEffect(() => {
    if (!hasLinkInUrl) return;
    const raw = new URLSearchParams(location.search).get('token');
    const token = raw?.trim() ?? '';
    navigate({ pathname: location.pathname, search: '', hash: '' }, { replace: true });
    if (raw === null) {
      // Query/fragment without a token: nothing to capture.
      if (tokenRef.current === null) setPhase(readFlag() ? 'gone' : 'request');
      return;
    }
    if (!TOKEN_SHAPE.test(token)) {
      tokenRef.current = null;
      writeFlag(false);
      setPhase('invalid');
      return;
    }
    if (tokenRef.current !== token) attemptedRef.current = false;
    tokenRef.current = token;
    writeFlag(true);
    setTemporaryError(null);
    setPhase('form');
  }, [hasLinkInUrl, location.search, location.pathname, navigate]);

  const forget = () => {
    tokenRef.current = null;
    attemptedRef.current = false;
    writeFlag(false);
  };

  if (phase === 'capturing') {
    return (
      <AuthLayout title="Opening your reset link" subtitle="One moment…">
        <p role="status" className="text-center text-sm text-fg-muted">Preparing a secure reset form…</p>
      </AuthLayout>
    );
  }

  if (phase === 'form') {
    return (
      <ConfirmForm
        temporaryError={temporaryError}
        onSubmit={async (newPassword, setFieldError) => {
          const token = tokenRef.current;
          if (!token) { setPhase(readFlag() ? 'gone' : 'request'); return; }
          const resubmission = attemptedRef.current;
          attemptedRef.current = true;
          setTemporaryError(null);
          try {
            await apiClient.post('/auth/password-reset/confirm', { token, newPassword });
            forget();
            notify.success('Password updated. You can now sign in.');
            navigate('/login', { replace: true });
          } catch (err) {
            const status = isApiError(err) ? err.status : 0;
            const code = isApiError(err) ? err.code : '';
            const passwordIssue = isApiError(err) ? err.fieldErrors.find((f) => /password/i.test(f.field)) : undefined;
            if (status === 400 && code !== 'TOKEN_INVALID_OR_EXPIRED' && passwordIssue) {
              // Rejected by request validation, before the token is consumed: the link is still good.
              attemptedRef.current = resubmission;
              setFieldError(passwordIssue.message);
              return;
            }
            if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
              forget();
              setPhase(resubmission ? 'used' : 'invalid');
              return;
            }
            // Network failure, timeout, 429 or 5xx: keep the token in memory so the user can retry.
            setTemporaryError(
              status === 429
                ? 'Too many attempts. Please wait a minute, then try again.'
                : 'We could not reach Nifty Hosting. Check your connection and try again — your link has not been used up by this.',
            );
          }
        }}
      />
    );
  }

  if (phase === 'invalid') {
    return (
      <AuthLayout title="This reset link is invalid or has expired" subtitle="Reset links expire after 30 minutes, work only once, and only the newest link is valid.">
        <p role="alert" className="mb-2 text-sm text-fg-muted">Request a new reset link below.</p>
        <RequestForm />
      </AuthLayout>
    );
  }

  if (phase === 'used') {
    return (
      <AuthLayout title="This reset link has already been used" subtitle="Reset links work only once.">
        <p role="alert" className="mb-6 text-sm text-fg-muted">
          If you just set a new password, sign in with it. Otherwise the link may have expired — request a new one below.
        </p>
        <Link to="/login" replace className="block h-12 w-full rounded-lg bg-brand px-4 text-center font-semibold leading-[3rem] text-brand-fg shadow-sm transition hover:brightness-110">Go to sign in</Link>
        <RequestForm />
      </AuthLayout>
    );
  }

  if (phase === 'gone') {
    return (
      <AuthLayout title="Open your reset link again" subtitle="For your security, reset links are not kept when this page reloads.">
        <p role="status" className="mb-2 text-sm text-fg-muted">
          Open the link from your email again (it is valid for 30 minutes and works once), or request a new one.
        </p>
        <RequestForm onSent={() => writeFlag(false)} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset password" subtitle="We'll email you a secure reset link.">
      <RequestForm />
    </AuthLayout>
  );
}

function ConfirmForm({ onSubmit, temporaryError }: {
  onSubmit: (newPassword: string, setFieldError: (message: string) => void) => Promise<void>;
  temporaryError: string | null;
}) {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const form = useForm<ConfirmValues>({
    resolver: zodResolver(confirmSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });
  const submit = form.handleSubmit(({ newPassword }) =>
    onSubmit(newPassword, (message) => form.setError('newPassword', { message })),
  );

  return (
    <AuthLayout title="Create a new password" subtitle="Use at least 12 characters and keep it unique to Nifty Hosting.">
      <form onSubmit={submit} className="space-y-5" noValidate aria-label="Set a new password">
        <div>
          <label htmlFor="new-password" className="mb-2 block text-sm font-medium text-fg">New password</label>
          <div className="relative">
            <input
              id="new-password"
              {...form.register('newPassword')}
              type={showNewPassword ? 'text' : 'password'}
              autoComplete="new-password"
              autoFocus
              className={fieldClass}
            />
            <button type="button" onClick={() => setShowNewPassword((value) => !value)}
              className="absolute inset-y-0 right-0 flex items-center px-4 text-sm font-medium text-fg-muted hover:text-fg"
              aria-label={showNewPassword ? 'Hide password' : 'Show password'}>
              {showNewPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {form.formState.errors.newPassword && (
            <p className="mt-2 text-sm text-danger">{form.formState.errors.newPassword.message}</p>
          )}
        </div>
        <div>
          <label htmlFor="confirm-password" className="mb-2 block text-sm font-medium text-fg">Confirm new password</label>
          <div className="relative">
            <input
              id="confirm-password"
              {...form.register('confirmPassword')}
              type={showConfirmation ? 'text' : 'password'}
              autoComplete="new-password"
              className={fieldClass}
            />
            <button type="button" onClick={() => setShowConfirmation((value) => !value)}
              className="absolute inset-y-0 right-0 flex items-center px-4 text-sm font-medium text-fg-muted hover:text-fg"
              aria-label={showConfirmation ? 'Hide password' : 'Show password'}>
              {showConfirmation ? 'Hide' : 'Show'}
            </button>
          </div>
          {form.formState.errors.confirmPassword && (
            <p className="mt-2 text-sm text-danger">{form.formState.errors.confirmPassword.message}</p>
          )}
        </div>
        {temporaryError && <p role="alert" className="text-sm text-danger">{temporaryError}</p>}
        <button type="submit" disabled={form.formState.isSubmitting} className={buttonClass}>
          {form.formState.isSubmitting ? 'Updating…' : temporaryError ? 'Try again' : 'Update password'}
        </button>
        <Link to="/login" className="block text-center text-sm text-brand hover:underline">Back to sign in</Link>
      </form>
    </AuthLayout>
  );
}

/** Always the same neutral confirmation: the API answers 202 whether or not the account exists. */
function RequestForm({ onSent }: { onSent?: () => void }) {
  const [sent, setSent] = useState(false);
  const form = useForm<RequestValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: { email: '' },
  });

  const onRequest = form.handleSubmit(async (values) => {
    try {
      await apiClient.post('/auth/password-reset/request', values);
      setSent(true);
      onSent?.();
    } catch (err) {
      const status = isApiError(err) ? err.status : 0;
      notify.error(status === 429 ? 'Too many requests. Please wait a few minutes and try again.' : 'Could not send a reset email right now. Please try again shortly.');
    }
  });

  if (sent) {
    return (
      <div className="mt-6 space-y-4">
        <p role="status" className="rounded-lg bg-surface-raised px-4 py-3 text-sm text-fg">
          If an account exists for that address, a reset link is on its way. It expires in 30 minutes.
        </p>
        <Link to="/login" className="block rounded-lg px-4 py-3 text-center text-sm font-medium text-brand hover:bg-surface-raised">
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={onRequest} className="mt-2 space-y-5" noValidate aria-label="Request a password reset link">
      <div>
        <label htmlFor="reset-email" className="mb-2 block text-sm font-medium text-fg">Email</label>
        <input
          id="reset-email"
          {...form.register('email')}
          type="email"
          autoComplete="username"
          autoFocus
          className={fieldClass}
        />
        {form.formState.errors.email && (
          <p className="mt-2 text-sm text-danger">{form.formState.errors.email.message}</p>
        )}
      </div>
      <button type="submit" disabled={form.formState.isSubmitting} className={buttonClass}>
        {form.formState.isSubmitting ? 'Sending…' : 'Send reset link'}
      </button>
      <Link to="/login" className="block text-center text-sm text-brand hover:underline">Back to sign in</Link>
    </form>
  );
}
